import type { DataItemId } from '@pn/core/domain/data';
import {
  isDynamicWorkspaceItem,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { apiAnnotationMapper } from '@pn/services/api/annotation/apiAnnotationMapper';
import { apiLayerItemMapper } from '@pn/services/api/layer/apiLayerMapper';
import {
  apiListMapper,
  toApiListLayersDefinition,
} from '@pn/services/api/list/apiListMapper';
import { pnApiClient } from '@pn/services/api/pnApiClient';

const copyLayer = async (item: WorkspaceItem) => {
  return pnApiClient.request<{ id: string; count: number }>({
    method: 'POST',
    url: `v2/layers/${item.id}/copy`,
    payload: apiLayerItemMapper.toOriginalItem(item),
  });
};

export type ListMembership = { found: DataItemId[]; missing: DataItemId[] };

/**
 * The API reports which of a list's ids resolve, but only for static lists;
 * both arrays are empty for dynamic ones, which have no membership to report.
 */
function toListMembership(
  item: WorkspaceItem,
  membership: ListMembership
): ListMembership | undefined {
  return isDynamicWorkspaceItem(item) ? undefined : membership;
}

const createList = async (
  item: WorkspaceItem
): Promise<{ id: string; membership: ListMembership | undefined }> => {
  const { id, found, missing } = await pnApiClient.request<
    { id: string } & ListMembership
  >({
    method: 'POST',
    url: 'v2/lists',
    payload: apiListMapper.toOriginalItem(item),
  });

  return { id, membership: toListMembership(item, { found, missing }) };
};

const createAnnotation = async (item: WorkspaceItem) => {
  return pnApiClient.request<{ id: string }>({
    method: 'POST',
    url: 'v2/annotations',
    payload: apiAnnotationMapper.toOriginalItem(item),
  });
};

const updateLayer = async (item: WorkspaceItem) => {
  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v2/layers/${item.id}`,
    payload: apiLayerItemMapper.toOriginalItem(item),
  });
};

const updateList = async (
  item: WorkspaceItem
): Promise<ListMembership | undefined> => {
  const { found, missing } = await pnApiClient.request<ListMembership>({
    method: 'PUT',
    url: `v2/lists/${item.id}`,
    payload: apiListMapper.toOriginalItem(item),
  });

  return toListMembership(item, { found, missing });
};

const updateAnnotation = async (item: WorkspaceItem) => {
  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v2/annotations/${item.id}`,
    payload: apiAnnotationMapper.toOriginalItem(item),
  });
};

const renameItem = async (item: WorkspaceItem, name: string) => {
  return pnApiClient.request<void>({
    method: 'PUT',
    url: `v2/${toApiType(item.itemType)}/${item.id}`,
    payload: { name },
  });
};

const deleteItem = async (item: WorkspaceItem) => {
  return pnApiClient.request<void>({
    method: 'DELETE',
    url: `v2/${toApiType(item.itemType)}/${item.id}`,
  });
};

const shareItem = async (item: WorkspaceItem) => {
  return pnApiClient.request<void>({
    method: 'POST',
    url: `v2/${toApiType(item.itemType)}/${item.id}/share`,
  });
};

const unshareItem = async (item: WorkspaceItem) => {
  return pnApiClient.request<void>({
    method: 'POST',
    url: `v2/${toApiType(item.itemType)}/${item.id}/unshare`,
  });
};

const addLayersDefinitionOverride = async (item: WorkspaceItem) => {
  return pnApiClient.request<void>({
    method: 'POST',
    url: `v2/${toApiType(item.itemType)}/${item.id}/add_layers_definition_override`,
    payload: {
      definition:
        item.itemType === 'list'
          ? toApiListLayersDefinition(item)
          : apiLayerItemMapper.toOriginalItem(item).layers_definition,
    },
  });
};

const removeLayersDefinitionOverride = async (item: WorkspaceItem) => {
  return pnApiClient.request<void>({
    method: 'POST',
    url: `v2/${toApiType(item.itemType)}/${item.id}/remove_layers_definition_override`,
  });
};

export const apiWorkspaceItemsProvider = {
  copyLayer,
  createList,
  createAnnotation,
  updateLayer,
  updateList,
  updateAnnotation,
  renameItem,
  deleteItem,
  shareItem,
  unshareItem,
  addLayersDefinitionOverride,
  removeLayersDefinitionOverride,
};

function toApiType(itemType: WorkspaceItem['itemType']): string {
  switch (itemType) {
    case 'annotation':
    case 'drawing':
      return 'annotations';
    case 'layer':
      return 'layers';
    case 'list':
      return 'lists';
    default:
      throw new Error(`Unsupported item type: ${itemType}`);
  }
}
